import { withFormik } from 'formik';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { redirectToOnboarding, segmentTrack } from 'src/app/state/actions';
import type { Dispatch, RootState } from 'src/app/state/store';

import { ZipCheckStatus } from './constants';
import { validation } from './validation';

import { ZipCodeDeliverableCheckComponent } from '.';

const mapStateToProps = (state: RootState) => {
  return {
    checkZipCodeEligibility: state.client.checkZipCodeEligibility,
    getFacilityByZipCode: state.client.getFacilityByZipCode,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchSegmentTrack: (
    event: UnifiedEvent,
    payload: {
      zipcode: string;
      pagepath: string;
    }
  ) => dispatch(segmentTrack(event, payload)),
  onRedirectToOnboarding: () => dispatch(redirectToOnboarding('Zip Checker Page')),
});
const connector = connect(mapStateToProps, mapDispatchToProps);

export type PropsFromRedux = ConnectedProps<typeof connector>;

const handleSubmit = async (values, { props, setSubmitting, setStatus, setErrors, validateForm, setFieldValue }) => {
  validateForm();
  const { checkZipCodeEligibility, getFacilityByZipCode, dispatchSegmentTrack, isProvider } = props;
  const zipcode = values.zipcode;

  setSubmitting(true);
  try {
    const response = await checkZipCodeEligibility(zipcode);
    const isDeliverable = response.data.isDeliverable;
    let facility = null;
    if (isDeliverable && isProvider) {
      const facilityResponse = await getFacilityByZipCode(zipcode);
      if (facilityResponse.status === 200) facility = facilityResponse.data;
    }
    // Set deliverable to null first to reset fade in text animation
    setStatus({ isDeliverable: null, lastCheckedZip: zipcode });
    setStatus({
      isDeliverable: isDeliverable ? ZipCheckStatus.isDeliverable : ZipCheckStatus.isNotDeliverable,
      lastCheckedZip: zipcode,
      facility,
    });
    const deliverableEvent = isDeliverable
      ? UNIFIED.deliveryAreaEligibilityZipcodeSuccessful
      : UNIFIED.deliveryAreaEligibilityZipcodeFailed;
    dispatchSegmentTrack(deliverableEvent, { zipcode, pagepath: window.location.pathname });

    if (!isDeliverable) setFieldValue('zipcode', '');
  } catch (error) {
    setErrors({
      _generic: 'Oops. Something went wrong, please try again.',
    });
  } finally {
    setSubmitting(false);
  }
};

const enhancedWithFormik = withFormik({
  mapPropsToValues: () => validation.initialValues,
  mapPropsToStatus: () => ({ isDeliverable: ZipCheckStatus.default, lastCheckedZip: '', facility: null }),
  validationSchema: validation.schema,
  validateOnChange: false,
  validateOnBlur: false,
  validateOnMount: false,
  handleSubmit,
});

export const ZipCodeDeliverableCheck = compose(connector, enhancedWithFormik)(ZipCodeDeliverableCheckComponent);
